import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '../../components/UI/card';
import { Button } from '../../components/UI/button';
import { Input } from '../../components/UI/input';
import { Label } from '../../components/UI/label';
import { Loader2, Save, Building, Mail, Server, Clock, AlertCircle } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

/**
 * PAGE: CONFIGURATION PAIEMENTS ENTREPRISES
 * 
 * Configuration email/FTP pour les appels à cotisation
 */

const EnterprisePaymentConfig = () => {
  const { toast } = useToast();

  const [enterprises, setEnterprises] = useState([]);
  const [selectedEnterpriseId, setSelectedEnterpriseId] = useState('');
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    contactEmail: '',
    ccEmails: '',
    ftpEnabled: false,
    ftpHost: '',
    ftpPort: 21,
    ftpUsername: '',
    ftpPassword: '',
    ftpPath: '/',
    autoSendEnabled: false,
    sendDay: 5,
    sendTime: '09:00'
  });

  useEffect(() => {
    loadEnterprises();
  }, []);

  useEffect(() => {
    if (selectedEnterpriseId) {
      loadConfig();
    }
  }, [selectedEnterpriseId]);

  const loadEnterprises = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/enterprises/options`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setEnterprises((response.data.data || []).map(e => ({
        id: e.value || e.id,
        nom: e.label || e.nom,
        sigle: null
      })));
    } catch (error) {
      console.error('Erreur chargement entreprises:', error);
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: 'Impossible de charger les entreprises'
      });
    } finally {
      setLoading(false);
    }
  };

  const loadConfig = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/contribution-calls/enterprises/${selectedEnterpriseId}/payment-config`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const configData = response.data.data;
      setConfig(configData);
      setFormData({
        contactEmail: configData.contactEmail || '',
        ccEmails: configData.ccEmails?.join(', ') || '',
        ftpEnabled: configData.ftpEnabled || false,
        ftpHost: configData.ftpHost || '',
        ftpPort: configData.ftpPort || 21,
        ftpUsername: configData.ftpUsername || '',
        ftpPassword: '', // Ne pas pré-remplir le mot de passe
        ftpPath: configData.ftpPath || '/',
        autoSendEnabled: configData.autoSendEnabled || false,
        sendDay: configData.sendDay || 5,
        sendTime: configData.sendTime || '09:00'
      });
    } catch (error) {
      // Config n'existe pas encore, c'est normal
      if (error.response?.status !== 404) {
        console.error('Erreur chargement config:', error);
      }
      setConfig(null);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.contactEmail) {
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: 'Email de contact obligatoire'
      });
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('accessToken');

      // Préparer les données
      const payload = {
        contactEmail: formData.contactEmail,
        ccEmails: formData.ccEmails
          ? formData.ccEmails.split(',').map(e => e.trim()).filter(e => e)
          : [],
        ftpEnabled: formData.ftpEnabled,
        autoSendEnabled: formData.autoSendEnabled
      };

      if (formData.ftpEnabled) {
        payload.ftpHost = formData.ftpHost;
        payload.ftpPort = formData.ftpPort;
        payload.ftpUsername = formData.ftpUsername;
        payload.ftpPath = formData.ftpPath;
        if (formData.ftpPassword) {
          payload.ftpPassword = formData.ftpPassword;
        }
      }

      if (formData.autoSendEnabled) {
        payload.sendDay = formData.sendDay;
        payload.sendTime = formData.sendTime;
      }

      await axios.put(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/contribution-calls/enterprises/${selectedEnterpriseId}/payment-config`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast({
        title: 'Succès',
        description: 'Configuration enregistrée avec succès'
      });

      // Recharger la config
      await loadConfig();
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: error.response?.data?.message || 'Erreur lors de la sauvegarde'
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading && !selectedEnterpriseId) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">

      {/* Sélection entreprise */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Building className="w-5 h-5 mr-2" />
            Sélectionner une entreprise
          </CardTitle>
        </CardHeader>
        <CardContent>
          <select
            value={selectedEnterpriseId}
            onChange={(e) => setSelectedEnterpriseId(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
          >
            <option value="">-- Sélectionnez une entreprise --</option>
            {enterprises.map(enterprise => (
              <option key={enterprise.id} value={enterprise.id}>
                {enterprise.nom} {enterprise.sigle && `(${enterprise.sigle})`}
              </option>
            ))}
          </select>
        </CardContent>
      </Card>

      {/* Formulaire de configuration */}
      {selectedEnterpriseId && (
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Configuration Email */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Mail className="w-5 h-5 mr-2" />
                Configuration Email
              </CardTitle>
              <CardDescription>
                Paramètres d'envoi des appels à cotisation par email
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="contactEmail">
                  Email de contact <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="contactEmail"
                  type="email"
                  value={formData.contactEmail}
                  onChange={(e) => handleChange('contactEmail', e.target.value)}
                  placeholder="contact@entreprise.com"
                  required
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Email principal pour recevoir les appels
                </p>
              </div>

              <div>
                <Label htmlFor="ccEmails">Emails en copie (optionnel)</Label>
                <Input
                  id="ccEmails"
                  type="text"
                  value={formData.ccEmails}
                  onChange={(e) => handleChange('ccEmails', e.target.value)}
                  placeholder="email1@entreprise.com, email2@entreprise.com"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Séparez les emails par des virgules
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Configuration FTP */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Server className="w-5 h-5 mr-2" />
                Configuration FTP
              </CardTitle>
              <CardDescription>
                Envoi automatique des fichiers sur un serveur FTP
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-2">
                <input
                  id="ftpEnabled"
                  type="checkbox"
                  checked={formData.ftpEnabled}
                  onChange={(e) => handleChange('ftpEnabled', e.target.checked)}
                  className="rounded border-gray-300"
                />
                <Label htmlFor="ftpEnabled">Activer l'envoi FTP</Label>
              </div>

              {formData.ftpEnabled && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="ftpHost">Hôte FTP</Label>
                      <Input
                        id="ftpHost"
                        type="text"
                        value={formData.ftpHost}
                        onChange={(e) => handleChange('ftpHost', e.target.value)}
                        placeholder="ftp.entreprise.com"
                      />
                    </div>

                    <div>
                      <Label htmlFor="ftpPort">Port</Label>
                      <Input
                        id="ftpPort"
                        type="number"
                        value={formData.ftpPort}
                        onChange={(e) => handleChange('ftpPort', parseInt(e.target.value))}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="ftpUsername">Nom d'utilisateur</Label>
                      <Input
                        id="ftpUsername"
                        type="text"
                        value={formData.ftpUsername}
                        onChange={(e) => handleChange('ftpUsername', e.target.value)}
                      />
                    </div>

                    <div>
                      <Label htmlFor="ftpPassword">Mot de passe</Label>
                      <Input
                        id="ftpPassword"
                        type="password"
                        value={formData.ftpPassword}
                        onChange={(e) => handleChange('ftpPassword', e.target.value)}
                        placeholder={config?.ftpPassword ? '********' : ''}
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="ftpPath">Chemin distant</Label>
                    <Input
                      id="ftpPath"
                      type="text"
                      value={formData.ftpPath}
                      onChange={(e) => handleChange('ftpPath', e.target.value)}
                      placeholder="/cotisations"
                    />
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Planification automatique */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Clock className="w-5 h-5 mr-2" />
                Envoi Automatique
              </CardTitle>
              <CardDescription>
                Planifiez l'envoi automatique des appels chaque mois
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-2">
                <input
                  id="autoSendEnabled"
                  type="checkbox"
                  checked={formData.autoSendEnabled}
                  onChange={(e) => handleChange('autoSendEnabled', e.target.checked)}
                  className="rounded border-gray-300"
                />
                <Label htmlFor="autoSendEnabled">Activer l'envoi automatique</Label>
              </div>

              {formData.autoSendEnabled && (
                <>
                  <Card className="bg-amber-50 border-amber-200">
                    <CardContent className="p-4">
                      <div className="flex items-start space-x-2">
                        <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                        <div className="text-sm text-amber-800">
                          <p className="font-semibold mb-1">À venir</p>
                          <p>
                            La fonctionnalité d'envoi automatique planifié sera disponible
                            dans une prochaine version.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 opacity-50 pointer-events-none">
                    <div>
                      <Label htmlFor="sendDay">Jour du mois</Label>
                      <Input
                        id="sendDay"
                        type="number"
                        min="1"
                        max="31"
                        value={formData.sendDay}
                        onChange={(e) => handleChange('sendDay', parseInt(e.target.value))}
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Entre 1 et 31
                      </p>
                    </div>

                    <div>
                      <Label htmlFor="sendTime">Heure d'envoi</Label>
                      <Input
                        id="sendTime"
                        type="time"
                        value={formData.sendTime}
                        onChange={(e) => handleChange('sendTime', e.target.value)}
                      />
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Boutons d'action */}
          <div className="flex gap-3">
            <Button
              type="submit"
              disabled={saving}
              className="flex-1"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Enregistrer la configuration
                </>
              )}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
};

export default EnterprisePaymentConfig;
